import { useState, useRef, ChangeEvent, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getApiUrl, getApiHeaders, apiFetch } from '../utils/api';
import './MaterialsPage.css';

interface WeightData {
    value: number | null;
    unit: string;
    source: string | null;
}

interface DimensionsData {
    length: number | null;
    width: number | null;
    height: number | null;
    unit: string;
    source: string | null;
}

interface MaterialItem {
    code: string;
    name: string;
    quantity: number;
    unit: string;
    unitCost: number | null;
    totalCost: number | null;
    currency: string;
    weight?: WeightData;
    dimensions?: DimensionsData;
}

interface ScannedMaterials {
    documentType: string;
    documentNumber: string;
    date: string;
    supplier: {
        name: string;
        contact: string;
    };
    items: MaterialItem[];
    totals: {
        subtotal: number;
        tax: number;
        total: number;
        currency: string;
    };
    confidence: number;
}

interface Material {
    id: string;
    name: string;
    code: string;
    quantity: number;
    unit: string;
    unitCost: number;
    currency: string;
    supplier: string;
    category: string;
}

function MaterialsPage() {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [showLogUse, setShowLogUse] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
    const [logUseAmount, setLogUseAmount] = useState('');
    const [logUseReason, setLogUseReason] = useState('');
    const [isLoggingUse, setIsLoggingUse] = useState(false);
    const [image, setImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [scannedData, setScannedData] = useState<ScannedMaterials | null>(null);
    const [editableItems, setEditableItems] = useState<MaterialItem[]>([]);

    // AI Natural Language State
    const [aiText, setAiText] = useState('');
    const [isParsingAi, setIsParsingAi] = useState(false);
    const [aiPreviewItems, setAiPreviewItems] = useState<any[]>([]);
    const [aiSummary, setAiSummary] = useState('');
    const [showAiInput, setShowAiInput] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    // Load materials from inventory API on mount
    useEffect(() => {
        const loadMaterials = async () => {
            try {
                const data = await apiFetch('/api/inventory/items');
                if (data.items) {
                    setMaterials(data.items.map((item: any) => ({
                        id: item.id,
                        name: item.name,
                        code: item.code,
                        quantity: item.quantity,
                        unit: item.unit,
                        unitCost: item.unitCost || 0,
                        currency: item.currency || 'EUR',
                        supplier: item.supplier || '',
                        category: item.source || 'other'
                    })));
                }
            } catch (error) {
                console.error('Failed to load materials:', error);
            }
        };
        loadMaterials();
    }, []);

    const categories = [
        { id: 'clay', icon: '🏺', name: 'Clay' },
        { id: 'metal', icon: '⚙️', name: 'Metal' },
        { id: 'wood', icon: '🪵', name: 'Wood' },
        { id: 'fabric', icon: '🧵', name: 'Fabric' },
        { id: 'paint', icon: '🎨', name: 'Paint' },
        { id: 'other', icon: '📦', name: 'Other' }
    ];

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const preview = URL.createObjectURL(file);
        setImage(preview);
        processMaterials(file);
    };

    const processMaterials = async (file: File) => {
        setIsProcessing(true);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(getApiUrl('/api/materials/scan'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('artisan_token')}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to scan materials document');
            }

            const data = await response.json();
            setScannedData(data.materials);
            setEditableItems(data.materials.items.map((item: MaterialItem) => ({
                ...item,
                weight: item.weight || { value: null, unit: 'kg', source: null },
                dimensions: item.dimensions || { length: null, width: null, height: null, unit: 'cm', source: null }
            })));
            toast.success('Document scanned successfully!');
        } catch (error) {
            console.error('Scan error:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to scan document');
            setImage(null);
        } finally {
            setIsProcessing(false);
        }
    };

    const updateItem = (index: number, field: string, value: string | number | null) => {
        setEditableItems(items =>
            items.map((item, i) => {
                if (i !== index) return item;

                if (field.startsWith('weight.')) {
                    const weightField = field.split('.')[1] as keyof WeightData;
                    return {
                        ...item,
                        weight: { ...item.weight!, [weightField]: value }
                    };
                }
                if (field.startsWith('dimensions.')) {
                    const dimField = field.split('.')[1] as keyof DimensionsData;
                    return {
                        ...item,
                        dimensions: { ...item.dimensions!, [dimField]: value }
                    };
                }
                return { ...item, [field]: value };
            })
        );
    };

    const confirmMaterials = async () => {
        if (!scannedData) return;

        try {
            const itemsToAdd = editableItems.map((item) => ({
                code: item.code || `MAT-${Date.now().toString(36).toUpperCase()}`,
                name: item.name,
                quantity: item.quantity,
                unit: item.unit,
                unitCost: item.unitCost,
                totalCost: item.totalCost,
                currency: item.currency,
                weight: item.weight,
                dimensions: item.dimensions,
                supplier: scannedData.supplier?.name
            }));

            const apiHeaders = await getApiHeaders();
            const response = await fetch(getApiUrl('/api/inventory/items'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...apiHeaders
                },
                body: JSON.stringify({
                    items: itemsToAdd,
                    source: 'materials'
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save to inventory');
            }

            const data = await response.json();
            toast.success(`${data.itemsAdded} items added to inventory!`);
            resetScanner();
        } catch (error) {
            console.error('Failed to update inventory:', error);
            toast.error('Failed to update inventory');
        }
    };

    const resetScanner = () => {
        if (image) URL.revokeObjectURL(image);
        setImage(null);
        setScannedData(null);
        setEditableItems([]);
        setShowScanner(false);
    };

    const openLogUseModal = (material: Material) => {
        setSelectedMaterial(material);
        setLogUseAmount('');
        setLogUseReason('');
        setShowLogUse(true);
    };

    const handleLogUse = async () => {
        if (!selectedMaterial || !logUseAmount) {
            toast.error('Please enter an amount');
            return;
        }

        const amount = parseFloat(logUseAmount);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        if (amount > selectedMaterial.quantity) {
            toast.error(`Not enough stock. Available: ${selectedMaterial.quantity} ${selectedMaterial.unit}`);
            return;
        }

        setIsLoggingUse(true);
        try {
            const response = await fetch(getApiUrl(`/api/materials/${selectedMaterial.id}/adjust-stock`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getApiHeaders()
                },
                body: JSON.stringify({
                    quantity: -amount,
                    reason: logUseReason || 'Material used for production'
                })
            });

            if (!response.ok) {
                throw new Error('Failed to log material usage');
            }

            setMaterials(prev => prev.map(m =>
                m.id === selectedMaterial.id
                    ? { ...m, quantity: m.quantity - amount }
                    : m
            ));

            toast.success(`Used ${amount} ${selectedMaterial.unit} of ${selectedMaterial.name}`);
            setShowLogUse(false);
            setSelectedMaterial(null);
        } catch (error) {
            console.error('Failed to log usage:', error);
            toast.error('Failed to log material usage');
        } finally {
            setIsLoggingUse(false);
        }
    };

    const handleAiParse = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!aiText.trim()) return;

        setIsParsingAi(true);
        try {
            const response = await fetch(getApiUrl('/api/materials/ai-parse'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getApiHeaders()
                },
                body: JSON.stringify({ text: aiText })
            });

            const data = await response.json();
            if (data.success) {
                setAiPreviewItems(data.items || []);
                setAiSummary(data.summary || '');
                if (data.items?.length === 0) {
                    toast.info('No materials identified. Try being more specific.');
                }
            } else {
                toast.error('Failed to parse text');
            }
        } catch (error) {
            console.error('AI Parse failed:', error);
            toast.error('AI parsing error');
        } finally {
            setIsParsingAi(false);
        }
    };

    const confirmAiDeduction = async () => {
        if (aiPreviewItems.length === 0) return;

        setIsLoggingUse(true);
        try {
            const response = await fetch(getApiUrl('/api/materials/batch-adjust'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getApiHeaders()
                },
                body: JSON.stringify({
                    adjustments: aiPreviewItems.map(item => ({
                        materialId: item.materialId,
                        quantity: -item.quantity,
                        reason: `AI Log: ${item.originalText}`
                    }))
                })
            });

            const data = await response.json();
            if (data.success) {
                // Update local state
                const updatedMaterials = data.materials;
                setMaterials(prev => prev.map(m => {
                    const updated = updatedMaterials.find((u: any) => u.id === m.id);
                    return updated ? { ...m, quantity: Number(updated.quantity) } : m;
                }));

                toast.success(`Successfully logged ${aiPreviewItems.length} items`);
                setAiText('');
                setAiPreviewItems([]);
                setAiSummary('');
                setShowAiInput(false);
            } else {
                toast.error('Batch adjustment failed');
            }
        } catch (error) {
            console.error('Batch adjustment error:', error);
            toast.error('Failed to deduct items');
        } finally {
            setIsLoggingUse(false);
        }
    };

    return (
        <div className="materials-page animate-fade-in">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <span className="title-icon">🧱</span>
                        Raw Materials
                    </h1>
                    <p className="page-subtitle">
                        Track your clay, wood, metal, and other supplies
                    </p>
                </div>
                <div className="header-buttons">
                    <button className="btn btn-secondary" onClick={() => setShowAiInput(!showAiInput)}>
                        {showAiInput ? '✕ Close AI' : '🤖 AI Log'}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowScanner(true)}
                    >
                        <span>📄</span> Scan Invoice
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowAddForm(true)}
                    >
                        <span>+</span> Add Material
                    </button>
                </div>
            </div>

            {showAiInput && (
                <div className="ai-log-section animate-slide-down">
                    <div className="section-header">
                        <h2>🤖 Natural Language AI Log</h2>
                        <p>Tell the AI what you used (e.g., "Used 500g clay and half a liter of glaze")</p>
                    </div>

                    <form onSubmit={handleAiParse} className="ai-input-form">
                        <textarea
                            value={aiText}
                            onChange={(e) => setAiText(e.target.value)}
                            placeholder="Type here what you used..."
                            className="ai-textarea"
                            rows={3}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary ai-submit-btn"
                            disabled={isParsingAi || !aiText.trim()}
                        >
                            {isParsingAi ? '✨ Parsing...' : 'Analyze Usage ✨'}
                        </button>
                    </form>

                    {aiSummary && (
                        <div className="ai-result-preview">
                            <div className="ai-summary-text">
                                <strong>AI identifies:</strong> {aiSummary}
                            </div>

                            <div className="ai-items-review">
                                {aiPreviewItems.map((item, idx) => (
                                    <div key={idx} className="ai-review-item">
                                        <div className="item-main">
                                            <span className="item-name">{item.name}</span>
                                            <span className="item-qty">-{item.quantity} {item.unit}</span>
                                        </div>
                                        <div className="item-context">
                                            From text: "{item.originalText}"
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {aiPreviewItems.length > 0 && (
                                <div className="ai-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={confirmAiDeduction}
                                        disabled={isLoggingUse}
                                    >
                                        {isLoggingUse ? 'Logging...' : '✓ Confirm & Deduct All'}
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => { setAiPreviewItems([]); setAiSummary(''); }}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showScanner && !scannedData && (
                <div className="scan-section animate-slide-up">
                    <div className="scan-header">
                        <h2>Scan Materials / Products</h2>
                        <button className="btn-close" onClick={resetScanner}>×</button>
                    </div>

                    {!image && !isProcessing && (
                        <>
                            <div className="upload-zone" onClick={() => cameraInputRef.current?.click()}>
                                <span className="upload-icon">⚖️</span>
                                <h3>Scan Product or Invoice</h3>
                                <p>Take a photo of your product, invoice, or scale reading</p>
                            </div>

                            <div className="camera-buttons">
                                <button
                                    className="btn btn-primary btn-large"
                                    onClick={() => cameraInputRef.current?.click()}
                                >
                                    <span>📷</span>
                                    Take Photo
                                </button>
                                <button
                                    className="btn btn-secondary btn-large"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <span>🖼️</span>
                                    Upload
                                </button>
                            </div>

                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />

                            <div className="tips-section">
                                <h3>⚖️ Pro Tips for Accurate Data</h3>
                                <ul>
                                    <li><strong>Weight:</strong> Place product on a scale and photograph it - we'll read the weight automatically!</li>
                                    <li><strong>Dimensions:</strong> Write L x W x H on a piece of paper next to the product</li>
                                    <li><strong>SKU:</strong> Write the product name/code on paper if not visible</li>
                                    <li>Good lighting helps accuracy</li>
                                    <li>Works with handwritten notes too!</li>
                                </ul>
                            </div>

                            <div className="info-banner">
                                <span className="info-icon">📦</span>
                                <p>
                                    <strong>Why weight & dimensions?</strong> They help calculate accurate shipping costs for your products!
                                </p>
                            </div>
                        </>
                    )}

                    {isProcessing && (
                        <div className="processing-section animate-fade-in">
                            <div className="processing-animation">
                                <div className="loader-ring"></div>
                                <span className="processing-icon">⚖️</span>
                            </div>
                            <h2>Analyzing Image...</h2>
                            <p>Reading weight, dimensions, and product details</p>
                        </div>
                    )}
                </div>
            )}

            {scannedData && !isProcessing && (
                <div className="result-section animate-slide-up">
                    {image && (
                        <div className="scanned-image">
                            <img src={image} alt="Scanned document" />
                        </div>
                    )}

                    <div className="card extracted-data">
                        <div className="data-header">
                            <div>
                                <span className="doc-type">{scannedData.documentType}</span>
                                <h3 className="doc-number">{scannedData.documentNumber || 'Product Scan'}</h3>
                                {scannedData.date && <p className="doc-date">{scannedData.date}</p>}
                            </div>
                            <div className="confidence-badge">
                                <span className="confidence-value">{Math.round(scannedData.confidence * 100)}%</span>
                                <span className="confidence-label">Confidence</span>
                            </div>
                        </div>

                        {scannedData.supplier?.name && (
                            <div className="supplier-info">
                                <span className="supplier-label">Supplier</span>
                                <span className="supplier-name">{scannedData.supplier.name}</span>
                            </div>
                        )}

                        <div className="edit-instructions">
                            <span className="instruction-icon">✏️</span>
                            <p>
                                <strong>Review & Edit:</strong> Verify each item. Edit the name/SKU to match existing products.
                                Add weight and dimensions if not detected - this helps with shipping calculations!
                            </p>
                        </div>

                        <div className="items-section">
                            <h4>Items ({editableItems.length})</h4>
                            <div className="items-list editable">
                                {editableItems.map((item, index) => (
                                    <div key={index} className="item-row editable-item">
                                        <div className="item-inputs">
                                            <div className="input-group">
                                                <label>Product Name</label>
                                                <input
                                                    type="text"
                                                    value={item.name}
                                                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                    placeholder="Enter product name"
                                                />
                                            </div>
                                            <div className="input-group small">
                                                <label>SKU Code</label>
                                                <input
                                                    type="text"
                                                    value={item.code}
                                                    onChange={(e) => updateItem(index, 'code', e.target.value)}
                                                    placeholder="SKU-001"
                                                />
                                            </div>
                                        </div>

                                        <div className="item-details-row">
                                            <div className="detail-group">
                                                <label>Qty</label>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                                    min="0"
                                                />
                                            </div>
                                            <div className="detail-group">
                                                <label>Unit</label>
                                                <input
                                                    type="text"
                                                    value={item.unit}
                                                    onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                                />
                                            </div>
                                            {item.unitCost !== null && (
                                                <div className="detail-group">
                                                    <label>Cost</label>
                                                    <span className="cost-display">
                                                        {item.currency} {item.unitCost?.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="shipping-data">
                                            <div className="shipping-section">
                                                <h5>⚖️ Weight {item.weight?.source && <span className="source-tag">{item.weight.source}</span>}</h5>
                                                <div className="shipping-inputs">
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        value={item.weight?.value || ''}
                                                        onChange={(e) => updateItem(index, 'weight.value', e.target.value ? parseFloat(e.target.value) : null)}
                                                        placeholder="0.0"
                                                    />
                                                    <select
                                                        value={item.weight?.unit || 'kg'}
                                                        onChange={(e) => updateItem(index, 'weight.unit', e.target.value)}
                                                    >
                                                        <option value="kg">kg</option>
                                                        <option value="g">g</option>
                                                        <option value="lb">lb</option>
                                                        <option value="oz">oz</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="shipping-section">
                                                <h5>📐 Dimensions {item.dimensions?.source && <span className="source-tag">{item.dimensions.source}</span>}</h5>
                                                <div className="dimensions-inputs">
                                                    <div className="dim-input">
                                                        <label>L</label>
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={item.dimensions?.length || ''}
                                                            onChange={(e) => updateItem(index, 'dimensions.length', e.target.value ? parseFloat(e.target.value) : null)}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <span className="dim-separator">×</span>
                                                    <div className="dim-input">
                                                        <label>W</label>
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={item.dimensions?.width || ''}
                                                            onChange={(e) => updateItem(index, 'dimensions.width', e.target.value ? parseFloat(e.target.value) : null)}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <span className="dim-separator">×</span>
                                                    <div className="dim-input">
                                                        <label>H</label>
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={item.dimensions?.height || ''}
                                                            onChange={(e) => updateItem(index, 'dimensions.height', e.target.value ? parseFloat(e.target.value) : null)}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <select
                                                        value={item.dimensions?.unit || 'cm'}
                                                        onChange={(e) => updateItem(index, 'dimensions.unit', e.target.value)}
                                                    >
                                                        <option value="cm">cm</option>
                                                        <option value="m">m</option>
                                                        <option value="in">in</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {scannedData.totals && scannedData.totals.total > 0 && (
                            <div className="totals-section">
                                <div className="total-row">
                                    <span>Total</span>
                                    <span className="total-value">
                                        {scannedData.totals.currency} {scannedData.totals.total.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-secondary" onClick={resetScanner}>
                            ← Scan Another
                        </button>
                        <button className="btn btn-primary" onClick={confirmMaterials}>
                            ✓ Confirm & Add to Inventory
                        </button>
                    </div>
                </div>
            )}

            {!showScanner && !scannedData && (
                <>
                    <div className="category-grid">
                        {categories.map((cat) => (
                            <div key={cat.id} className="category-card">
                                <span className="category-icon">{cat.icon}</span>
                                <span className="category-name">{cat.name}</span>
                                <span className="category-count">0 items</span>
                            </div>
                        ))}
                    </div>

                    <div className="summary-cards">
                        <div className="summary-card">
                            <span className="summary-value">€{materials.reduce((sum, m) => sum + (m.quantity * m.unitCost), 0).toFixed(2)}</span>
                            <span className="summary-label">Total Value</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-value">{materials.length}</span>
                            <span className="summary-label">Total Items</span>
                        </div>
                        <div className="summary-card warning">
                            <span className="summary-value">{materials.filter(m => m.quantity < 10).length}</span>
                            <span className="summary-label">Low Stock</span>
                        </div>
                    </div>

                    {materials.length === 0 ? (
                        <div className="empty-state">
                            <span className="empty-icon">🧱</span>
                            <h3>No Materials Yet</h3>
                            <p>Start tracking your raw materials by adding them manually or scanning a delivery note</p>
                            <div className="empty-actions">
                                <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
                                    Add Material
                                </button>
                                <button className="btn btn-secondary" onClick={() => setShowScanner(true)}>
                                    Scan Invoice
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="materials-list">
                            {materials.map((material) => (
                                <div key={material.id} className="material-card">
                                    <div className="material-info">
                                        <h3>{material.name}</h3>
                                        <span className="material-code">{material.code}</span>
                                    </div>
                                    <div className="material-quantity">
                                        <span className="qty-value">{material.quantity}</span>
                                        <span className="qty-unit">{material.unit}</span>
                                    </div>
                                    <div className="material-cost">
                                        {material.currency} {material.unitCost.toFixed(2)} / {material.unit}
                                    </div>
                                    <button
                                        className="btn btn-use"
                                        onClick={() => openLogUseModal(material)}
                                    >
                                        📝 Log Use
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {showAddForm && (
                <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Add Material</h2>
                        <form onSubmit={(e) => { e.preventDefault(); setShowAddForm(false); }}>
                            <div className="form-group">
                                <label>Material Name</label>
                                <input type="text" placeholder="e.g., Red Clay" required />
                            </div>
                            <div className="form-group">
                                <label>Code (Optional)</label>
                                <input type="text" placeholder="e.g., RC-001" />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Quantity</label>
                                    <input type="number" defaultValue={0} min={0} />
                                </div>
                                <div className="form-group">
                                    <label>Unit</label>
                                    <select defaultValue="kg">
                                        <option value="kg">Kilograms (kg)</option>
                                        <option value="g">Grams (g)</option>
                                        <option value="m">Meters (m)</option>
                                        <option value="units">Units</option>
                                        <option value="liters">Liters</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Unit Cost</label>
                                    <input type="number" step="0.01" defaultValue={0} />
                                </div>
                                <div className="form-group">
                                    <label>Currency</label>
                                    <select defaultValue="EUR">
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Supplier</label>
                                <input type="text" placeholder="e.g., Local Clay Co." />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select defaultValue="other">
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.icon} {cat.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Add Material
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showLogUse && selectedMaterial && (
                <div className="modal-overlay" onClick={() => setShowLogUse(false)}>
                    <div className="modal log-use-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>📝 Log Material Usage</h2>
                        <div className="material-summary">
                            <span className="material-name">{selectedMaterial.name}</span>
                            <span className="available-stock">
                                Available: {selectedMaterial.quantity} {selectedMaterial.unit}
                            </span>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); handleLogUse(); }}>
                            <div className="form-group">
                                <label>Amount Used</label>
                                <div className="amount-input-group">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max={selectedMaterial.quantity}
                                        value={logUseAmount}
                                        onChange={(e) => setLogUseAmount(e.target.value)}
                                        placeholder="0"
                                        required
                                        autoFocus
                                    />
                                    <span className="unit-label">{selectedMaterial.unit}</span>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Reason (Optional)</label>
                                <input
                                    type="text"
                                    value={logUseReason}
                                    onChange={(e) => setLogUseReason(e.target.value)}
                                    placeholder="e.g., Made ceramic vase, Product order #123"
                                />
                            </div>
                            <div className="quick-amounts">
                                <span className="quick-label">Quick:</span>
                                {[1, 5, 10, 25].map(amt => (
                                    <button
                                        key={amt}
                                        type="button"
                                        className="quick-btn"
                                        onClick={() => setLogUseAmount(String(Math.min(amt, selectedMaterial.quantity)))}
                                        disabled={amt > selectedMaterial.quantity}
                                    >
                                        {amt}
                                    </button>
                                ))}
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowLogUse(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isLoggingUse}>
                                    {isLoggingUse ? 'Logging...' : '✓ Deduct from Stock'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MaterialsPage;
